<template>
  <div v-show="isActive" class="map-tool-window wms-tool-window" id="wmsUploadWindow">

    <!-- HEADER -->
    <div class="map-tool-window-header" id="wmsUploadWindowheader">
      <p class="map-tool-window-header-title">Subir WMS/WFS</p>
      <div class="map-tool-window-header-actions">
        <button class="control-btn" @click="exitTool">
          <svg viewBox="0 0 24 24"><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
        </button>
      </div>
    </div>

    <!-- BODY -->
    <div class="map-tool-window-body">
      <label class="wms-label">Tipo de servicio</label>
      <select v-model="serviceType" class="wms-select">
        <option value="auto">Auto (detectar)</option>
        <option value="wms-external">WMS</option>
        <option value="wfs-external">WFS</option>
        <option value="esri-rest">Esri REST (ArcGIS)</option>
      </select>

      <label class="wms-label">Ingrese URL del servicio</label>
      <textarea
        v-model="wmsUrl"
        class="wms-textarea"
        rows="2"
        placeholder="https://ejemplo.com/wms?SERVICE=WMS&REQUEST=GetCapabilities"
        spellcheck="false"
      />
      <div class="wms-footer">
        <button class="wms-btn" @click="exitTool">Cancelar</button>
        <button class="wms-btn wms-btn--primary" @click="onPreview" :disabled="loadingCaps">
          {{ loadingCaps ? "Conectando..." : "Conectar" }}
        </button>
      </div>

      <p v-if="capsMessage" class="wms-message" :class="{ 'wms-message--error': capsError }">
        {{ capsMessage }}
      </p>

      <!-- SELECCIÓN DE CAPAS DESDE GetCapabilities -->
      <div v-if="capsLayers.length" class="wms-add-section">
        <label class="wms-label">Nombre de la conexión</label>
        <input
          type="text"
          v-model="sourceLabel"
          class="wms-select"
          placeholder="Nombre para identificar este servidor"
        />

        <label class="wms-label">Capas disponibles</label>
        <div class="wms-caps-list">
          <label v-for="l in capsLayers" :key="l.name" class="wms-caps-item">
            <input type="checkbox" :value="l" v-model="selectedCapsLayers" />
            {{ l.title }}
          </label>
        </div>
        <button
          class="wms-btn wms-btn--primary wms-btn--block"
          @click="addSelectedLayers"
          :disabled="!selectedCapsLayers.length"
        >
          Añadir seleccionadas
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { draggableDiv } from '@/components/mixin/draggableDiv.js'
import { restLayerActivity } from '@/components/mixin/restLayerActivity.js'
import { mapService } from '@/services/map/mapService'
import { detectService } from '@/services/externalLayers'

export default {
  name: 'WmsUpload',
  mixins: [draggableDiv, restLayerActivity],
  data() {
    return {
      serviceType: 'auto',
      connector: null,
      wmsUrl: '',
      baseUrl: '',
      sourceLabel: '',
      loadingCaps: false,
      capsMessage: '',
      capsError: false,
      capsLayers: [],
      selectedCapsLayers: []
    }
  },
  computed: {
    isActive() {
      return this.$store.state.mapTools.wmsUpload.active
    },
    sessionLayers() {
      return this.$store.getters.getWmsSessionLayers
    }
  },
  mounted() {
    this.makeDraggable('wmsUploadWindow')
  },
  methods: {
    exitTool() {
      this.$store.dispatch('setMapToolSelection', { tool: 'wmsUpload', active: false })
      this.capsMessage = ''
      this.capsError = false
      this.capsLayers = []
      this.selectedCapsLayers = []
    },

    cleanUrl(u) {
      return u.split('?')[0]
    },

    generateId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
      }
      // Fallback para contextos no seguros (HTTP), donde crypto.randomUUID no existe.
      return 'wms-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
    },

    async onPreview() {
      this.capsMessage = ''
      this.capsError = false
      this.capsLayers = []
      this.selectedCapsLayers = []
      this.connector = null

      const u = this.wmsUrl.trim()
      if (!u) {
        this.capsMessage = 'Ingresa una URL.'
        this.capsError = true
        return
      }

      this.baseUrl = this.cleanUrl(u)

      this.loadingCaps = true
      try {
        const { connector, layers } = await detectService(this.baseUrl, this.serviceType)

        this.connector = connector
        try {
          this.sourceLabel = new URL(this.baseUrl).hostname
        } catch (e) {
          this.sourceLabel = this.baseUrl
        }

        this.capsLayers = layers
        this.capsMessage = `${layers.length} capa(s) disponible(s) (${connector.label}).`
      } catch (e) {
        console.error('[WmsUpload] Error al conectar:', e)
        this.capsMessage = e.name === 'AbortError'
          ? 'Tiempo de espera agotado al conectar con el servidor.'
          : (e.message || 'No se pudo conectar (probablemente CORS o URL incorrecta).')
        this.capsError = true
      } finally {
        this.loadingCaps = false
      }
    },

    addSelectedLayers() {
      if (!this.selectedCapsLayers.length || !this.connector) return

      const providerName = this.connector.id
      const sourceLabel = this.sourceLabel.trim() || this.baseUrl

      let added = false
      this.selectedCapsLayers.forEach(capsLayer => {
        const layerName = capsLayer.name
        const alreadyAdded = this.sessionLayers.some(
          l => l.provider.url === this.baseUrl && l.provider.layerName === layerName && l.provider.name === providerName
        )
        if (alreadyAdded) return

        const layer = {
          id: this.generateId(),
          name: capsLayer.title,
          opacity: 1,
          visible: true,
          loading: false,
          progress: null,
          provider: {
            name: providerName,
            url: this.baseUrl,
            layerName,
            sourceLabel,
            bbox: capsLayer.bbox,
            meta: capsLayer.meta
          }
        }

        mapService.addLayer(layer)
        this.$store.dispatch('addWmsSessionLayer', layer)
        this.startExternalLayerSession(layer, this.$route.params.idMap)
        added = true
      })

      this.selectedCapsLayers = []

      // Abre el panel "Lista de capas" para que el usuario vea/gestione la(s) capa(s) recién agregada(s).
      if (added && !this.$store.state.mapTools.layers.active) {
        this.$store.dispatch('setMapToolSelection', { tool: 'layers', active: true })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.wms-tool-window {
  left: 60vw;
  top: 4em;
  width: 340px;
}

.wms-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--color-graphite);
  margin-bottom: 0.5rem;
}

.wms-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.78rem;
  resize: vertical;
  outline: none;
  color: var(--color-graphite);
  background: var(--color-mist);
  line-height: 1.5;

  &:focus {
    border-color: var(--color-navy);
    background: var(--color-white);
  }
}

.wms-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.wms-btn {
  padding: 0.4rem 1rem;
  border-radius: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--color-line);
  background: var(--color-white);
  color: var(--color-graphite);

  &:hover {
    background: var(--color-mist);
  }

  &--primary {
    background: var(--color-navy);
    color: var(--color-white);
    border-color: var(--color-navy);

    &:hover {
      background: var(--color-graphite);
      border-color: var(--color-graphite);
    }

    &:disabled {
      background: var(--color-steel);
      border-color: var(--color-steel);
      cursor: not-allowed;
    }
  }

  &--block {
    width: 100%;
    margin-top: 0.5rem;
  }
}

.wms-message {
  margin-top: 0.6rem;
  font-size: 0.78rem;
  color: var(--color-graphite);

  &--error {
    color: var(--color-danger);
  }
}

.wms-select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.45rem 0.6rem;
  border: 1px solid var(--color-line);
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--color-graphite);
  background: var(--color-white);
  margin-bottom: 0.85rem;
}

.wms-add-section {
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid var(--color-line);
}

.wms-caps-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 0.6rem;
}

.wms-caps-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--color-graphite);
  cursor: pointer;

  input[type="checkbox"] {
    cursor: pointer;
  }
}
</style>
